import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import {
  MagnifyingGlassIcon,
  PhotoIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { TWModal, TWButton, TWInput, EmptyState } from '../../../ui';
import imageLibraryApi from '../../../../services/imageLibraryApi';
import './image-library-browse-modal.css';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 400;

const cn = (...classes) => classes.filter(Boolean).join(' ');

// Klasör filtresi pill'i — düz select yerine kataloğun yapısını gösterir.
const FolderPill = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition',
      active
        ? 'border-blue-600 bg-blue-600 text-white shadow-sm dark:border-sky-500 dark:bg-sky-500 dark:text-slate-900'
        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600',
    )}
  >
    {label}
    {count != null ? (
      <span className={active ? 'opacity-70' : 'text-slate-400'}> {count}</span>
    ) : null}
  </button>
);

// Yükleme sırasında shimmer'lı iskelet grid.
const SkeletonGrid = () => (
  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700"
      >
        <div className="ilb-skeleton aspect-square w-full" />
        <div className="ilb-skeleton mx-2.5 my-2.5 h-3 w-2/3 rounded" />
      </div>
    ))}
  </div>
);

// Ürün editöründen açılan library tarama galerisi. Bir resim seçilip
// "Use this image" ile onaylanınca onSelect(libraryImage) çağrılır.
const ImageLibraryBrowseModal = ({ open, onClose, onSelect }) => {
  const [images, setImages] = useState([]);
  const [folders, setFolders] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [folder, setFolder] = useState('');
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Modal her açıldığında filtreleri, seçimi ve grid'i sıfırla.
  useEffect(() => {
    if (open) {
      setSearch('');
      setDebouncedSearch('');
      setFolder('');
      setSelected(null);
      setImages([]);
      setLoading(true);
    }
  }, [open]);

  // Klasör listesini modal açılınca bir kez çek.
  useEffect(() => {
    if (!open) return;
    imageLibraryApi
      .folders()
      .then((res) => setFolders(res.data?.data || []))
      .catch(() => setFolders([]));
  }, [open]);

  const fetchImages = useCallback(
    async (pageNumber, searchText, folderValue) => {
      setLoading(true);
      try {
        const searchPart = searchText
          ? `&search=${encodeURIComponent(searchText)}`
          : '';
        const folderPart = folderValue
          ? `&folder=${encodeURIComponent(folderValue)}`
          : '';
        const query = `?pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}${searchPart}${folderPart}`;
        const res = await imageLibraryApi.list(query);
        const data = res.data?.data || [];
        setImages((prev) => (pageNumber === 1 ? data : [...prev, ...data]));
        setTotalRecords(res.data?.totalRecords || 0);
        setPage(pageNumber);
      } catch {
        message.error('Failed to load image library');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Filtre değişince ilk sayfadan çek (modal açıkken).
  useEffect(() => {
    if (open) fetchImages(1, debouncedSearch, folder);
  }, [open, debouncedSearch, folder, fetchImages]);

  const hasMore = images.length < totalRecords;
  const showSkeleton = loading && images.length === 0;
  const showEmpty = !loading && images.length === 0;

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  return (
    <TWModal
      open={open}
      onClose={onClose}
      title="Image library"
      description="Pick a photo from the shared catalog."
      size="4xl"
      showCloseButton
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            {selected ? (
              <>
                <img
                  src={selected.imagepath}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                />
                <div className="min-w-0 leading-tight">
                  <p className="truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
                    {selected.name}
                  </p>
                  <p className="text-[0.65rem] text-slate-400 dark:text-slate-500">
                    Ready to use
                  </p>
                </div>
              </>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Select a photo to continue.
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <TWButton label="Cancel" onClick={onClose} />
            <TWButton
              variant="primary"
              label="Use this image"
              disabled={!selected}
              onClick={handleConfirm}
            />
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <TWInput
              leftIcon={MagnifyingGlassIcon}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search photos by name or tag…"
              aria-label="Search image library"
            />
          </div>
          {totalRecords > 0 ? (
            <span className="hidden shrink-0 text-xs font-semibold tabular-nums text-slate-400 dark:text-slate-500 sm:block">
              {totalRecords.toLocaleString('en-US')} photos
            </span>
          ) : null}
        </div>

        {folders.length > 0 ? (
          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
            <FolderPill
              label="All"
              active={folder === ''}
              onClick={() => setFolder('')}
            />
            {folders.map((f) => (
              <FolderPill
                key={f.folder}
                label={f.folder}
                count={f.imagecount}
                active={folder === f.folder}
                onClick={() => setFolder(f.folder)}
              />
            ))}
          </div>
        ) : null}

        {showSkeleton ? (
          <SkeletonGrid />
        ) : showEmpty ? (
          <EmptyState
            variant="dashed"
            icon={PhotoIcon}
            title="No photos found"
            description="Try a different search term or folder."
          />
        ) : (
          <>
            <div className="grid max-h-[54vh] grid-cols-3 gap-3 overflow-y-auto pr-0.5 sm:grid-cols-4">
              {images.map((img, idx) => {
                const isSelected = selected?.id === img.id;
                return (
                  <button
                    type="button"
                    key={img.id}
                    onClick={() => setSelected(img)}
                    style={{
                      animationDelay: `${Math.min(idx % PAGE_SIZE, 11) * 35}ms`,
                    }}
                    className={cn(
                      'ilb-card group relative flex flex-col overflow-hidden rounded-xl border bg-white text-left transition-all duration-200 dark:bg-slate-800',
                      isSelected
                        ? '-translate-y-0.5 border-blue-500 shadow-lg shadow-blue-500/15 ring-2 ring-blue-500/30 dark:border-sky-500'
                        : 'border-slate-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:hover:border-slate-600',
                    )}
                  >
                    <div className="relative aspect-square w-full overflow-hidden bg-slate-100 dark:bg-slate-900">
                      {img.imagepath ? (
                        <img
                          src={img.imagepath}
                          alt={img.name}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-slate-400 dark:text-slate-600">
                          <PhotoIcon className="h-8 w-8" />
                        </div>
                      )}
                      {img.folder ? (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-2.5 pb-1.5 pt-6">
                          <span className="text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-white/85">
                            {img.folder}
                          </span>
                        </div>
                      ) : null}
                      {isSelected ? (
                        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow-md ring-2 ring-white dark:bg-sky-500 dark:text-slate-900 dark:ring-slate-800">
                          <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate px-2.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                      {img.name || '—'}
                    </p>
                  </button>
                );
              })}
            </div>
            {hasMore ? (
              <div className="flex justify-center pt-1">
                <TWButton
                  variant="subtle"
                  label={loading ? 'Loading…' : 'Load more photos'}
                  loading={loading}
                  onClick={() => fetchImages(page + 1, debouncedSearch, folder)}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </TWModal>
  );
};

export default ImageLibraryBrowseModal;
